import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { showSnack } from '@t7x000/apex-web/lib/redux/actions/snackbarActions.js';
import { useTranslation } from 'react-i18next';

import InstrumentRowContainer from '@t7x000/apex-web/lib/components/InstrumentRow';
import PriceChart from '@t7x000/apex-web/lib/components/PriceChart/PriceChartContainer';
import DepthChart from '@t7x000/apex-web/lib/components/DepthChart/DepthChartContainer';
import OrderHistoryComponent from '@t7x000/apex-web/lib/components/OrderHistoryComponents';
import MarginOrderHistoryComponent from '@t7x000/apex-web/lib/components/OrderHistoryComponents/MarginOrderHistory';
import FuturesOrderHistoryComponent from '@t7x000/apex-web/lib/components/OrderHistoryComponents/FuturesOrderHistory';
import OrderBook from '@t7x000/apex-web/lib/components/OrderBookComponents/OrderBookContainer';
import RecentTrades from '@t7x000/apex-web/lib/components/RecentTrades/RecentTradesContainer';
import OrderEntry from '@t7x000/apex-web/lib/components/OrderEntryComponents/defaultForm/OrderEntryContainer';
import FuturesOrderEntry from '@t7x000/apex-web/lib/components/OrderEntryComponents/futures/FuturesOrderEntryForm/FuturesOrderEntryFormContainer';
import FuturesOrderBook from '@t7x000/apex-web/lib/components/FuturesOrderBook/FuturesOrderBookContainer';
import FuturesRecentTrades from '@t7x000/apex-web/lib/components/FuturesRecentTrades/FuturesRecentTradesContainer';
import FuturesDepthChart from '@t7x000/apex-web/lib/components/DepthChart/FuturesDepthChartContainer';

import InstrumentPositionsWrapperContainer from '@t7x000/apex-web/lib/components/InstrumentPositionsWrapper';
import { getBEMClasses } from '@t7x000/apex-web/lib/helpers/cssClassesHelper';
import { isMarginActiveSelector, accountsSelector } from '@t7x000/apex-web/lib/redux/margin/selectors/marginSelectors';
import { isFuturesActiveSelector } from '@t7x000/apex-web/lib/redux/futures/selectors/futuresSelectors';
import { initSelectedInstrument } from '@t7x000/apex-web/lib/redux/actions/instrumentActions';
import {
  futuresInstrumentsSelector,
  futuresSelectedInstrumentIdSelector,
} from '@t7x000/apex-web/lib/redux/futures/selectors/futuresInstrumentsSelector';
import { isFuturesAccount, selectAccount } from '@t7x000/apex-web/lib/redux/actions/userActions';
import {
  instrumentSelectorEnabledInstruments,
  selectedInstrumentIdSelector,
} from '@t7x000/apex-web/lib/redux/selectors/instrumentPositionSelectors';
import { instrumentSelectorMarginInstruments } from '@t7x000/apex-web/lib/redux/margin/selectors/marginInstrumentSelectors';

import config from '../../config';

import './TradingPage.css';
import { useInstrumentUrlSync } from '@t7x000/apex-web/lib/components/InstrumentSelector/hooks/useInstrumentUrlSync';
import { useUiViewMode } from '../../context/UiViewModeContext';

const cls = getBEMClasses('exchange');

function getAccountTypeFromPath(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'exchange') {
    return null;
  }

  const accountSegment = segments[1];

  switch ((accountSegment || '').toLowerCase()) {
    case 'spot':
      return 'Asset';
    case 'margin':
      return 'Margin';
    case 'futures':
      return 'Futures';
    default:
      return 'Asset';
  }
}

const Tabs = ({ items, className, name }) => {
  const [activeTab, setActiveTab] = useState(items[0].id);

  return (
    <div className={`${className} ${cls('tabs')}`}>
      {items.map(item => (
        <label key={item.id}>
          {item.title}
          <input
            type="radio"
            name={name}
            aria-controls={item.id}
            checked={activeTab === item.id}
            onClick={() => setActiveTab(item.id)}
          />
        </label>
      ))}
    </div>
  );
};

Tabs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** id of the element a tab controls */
      id: PropTypes.string.isRequired,
      /** title of the tab */
      title: PropTypes.string.isRequired,
    }),
  ).isRequired,
  className: PropTypes.string.isRequired,
  /** name of the tabs group */
  name: PropTypes.string.isRequired,
};

const {
  OrderHistoryData: { usePagination },
  TradingLayout: { maxLines },
} = config;

const TradingLayout = ({
  isMarginActive,
  isFuturesActive,
  selectedInstrumentId,
  instruments,
  initSelectedInstrument,
  showSnack,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    const tradingServer = window.localStorage.getItem('tradingServer');
    const gatewayConfig = config.global.gateway;
    const requiresServerSelection = Array.isArray(gatewayConfig);

    if (requiresServerSelection && !tradingServer) {
      const message = t('Must select a trading server');
      showSnack({
        id: 'MissingTradingServer',
        text: message,
        type: 'warning',
      });
      history.replace('/login');
    }
  }, [history, showSnack, t]);

  const location = useLocation();

  const routePrefix = location.pathname.startsWith('/trade/margin')
    ? '/trade/margin'
    : location.pathname.startsWith('/trade/futures')
      ? '/trade/futures'
      : location.pathname.startsWith('/trade/spot')
        ? '/trade/spot'
        : '/trade';

  useInstrumentUrlSync({
    instruments,
    selectedInstrumentId,
    initSelectedInstrument,
    routePrefix,
  });

  const orderHistoryConfig = {
    usePagination,
    maxLines,
    filterMode: 'selectedInstrument',
  };

  const dispatch = useDispatch();
  const accounts = useSelector(accountsSelector);
  const selectedAccountId = useSelector(state => state.user.selectedAccountId);
  const selectedAccount = accounts.find(a => a.AccountId === selectedAccountId);
  const selectedAccountType = selectedAccount?.AccountType;

  const { isPro } = useUiViewMode();

  useEffect(
    () => {
      const type = getAccountTypeFromPath(location.pathname);
      if (!type || type === selectedAccountType) return;
      // Find first account of this type
      const account = accounts.find(a => a.AccountType === type);
      if (account) {
        dispatch(selectAccount(account.AccountId, account.OMSID));
      }
    },
    [location.pathname, accounts, dispatch, selectedAccountType],
  );

  return (
    <div className={cls('container')}>
      <div className={cls('instrument-row')}>
        <InstrumentRowContainer isFuturesActive={isFuturesActive} />
      </div>
      <div className={`${cls('content')} ${isPro ? '' : 'exchange__content--basic'}`}>
        <div className={cls('charts-area')}>
          <Tabs
            items={[{ id: 'price-chart', title: t('Price Chart') }, { id: 'depth-chart', title: t('Depth Chart') }]}
            name="charts"
            className={cls('charts-tabs')}
          />
          <div id="price-chart">
            <PriceChart />
          </div>
          <div id="depth-chart">{isFuturesActive ? <FuturesDepthChart smallScreen /> : <DepthChart smallScreen />}</div>
        </div>

        {isPro && <div className={cls('order-book-area')}>
          <Tabs
            items={[{ id: 'order-book', title: 'Order Book' }, { id: 'recent-trades', title: 'Recent Trades' }]}
            name="order-book"
            className={cls('order-book-tabs')}
          />
          <div id="order-book">
            <div className="trade-component__header">{t('Order Book')}</div>
            {isFuturesActive ? <FuturesOrderBook /> : <OrderBook />}
          </div>
          <div id="recent-trades">
            <div className="trade-component__header">{t('Recent Trades')}</div>
            {isFuturesActive ? <FuturesRecentTrades /> : <RecentTrades />}
          </div>
        </div>}
        <div className={cls('order-entry-area')}>
          <Tabs
            items={[{ id: 'order-entry', title: 'Order Entry' }, { id: 'balances', title: 'Balances' }]}
            name="order-entry"
            className={cls('order-entry-tabs')}
          />
          <div id="order-entry">
            <div className="trade-component__header">{t('Order Entry')}</div>
            {isFuturesActive ? <FuturesOrderEntry /> : <OrderEntry />}
          </div>
          <div id="balances">
            <InstrumentPositionsWrapperContainer />
          </div>
        </div>

        <div className={cls('orders-area')}>
          {isFuturesActive ? (
            <FuturesOrderHistoryComponent config={orderHistoryConfig} />
          ) : isMarginActive ? (
            <MarginOrderHistoryComponent config={orderHistoryConfig} />
          ) : (
            <OrderHistoryComponent config={orderHistoryConfig} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  const isFutures = isFuturesAccount(state);
  const isMarginActive = isMarginActiveSelector(state);
  const selectedInstrumentId = isFutures
    ? futuresSelectedInstrumentIdSelector(state)
    : selectedInstrumentIdSelector(state);
  const instruments = isFutures
    ? futuresInstrumentsSelector(state)
    : isMarginActive
      ? instrumentSelectorMarginInstruments(state)
      : instrumentSelectorEnabledInstruments(state);

  return {
    isMarginActive: isMarginActive,
    isFuturesActive: isFuturesActiveSelector(state),
    selectedInstrumentId,
    instruments,
  };
};

const mapDispatchToProps = {
  initSelectedInstrument,
  showSnack,
};

export const TradingPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TradingLayout);
